import {
  MeetingFragmentFragment,
  UpdateMeetingsLessonMutation,
  UpdateMeetingsLessonMutationVariables,
  UpdateMeetingsMutation,
  UpdateMeetingsMutationVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import { MeetingFragment } from "@outschool/ui-components-classroom";
import { dataIdFromObject } from "@outschool/ui-gql";
import React from "react";

import {
  CreateMeetingsMutation,
  CreateMeetingsMutationVariables,
  CreateMeetingsWithLessonUidsMutation,
  CreateMeetingsWithLessonUidsMutationVariables,
  DeleteMeetingMutation,
  DeleteMeetingMutationVariables,
} from "../../shared/generated/graphql";
import { teacherSectionQuery } from "./SectionQueries";

import type { ExecutionResult } from "@outschool/ui-apollo";

export const createMeetingsMutation = gql`
  mutation createMeetings($sectionUid: ID!, $times: [StartEndTime!]!) {
    createMeetings(sectionUid: $sectionUid, times: $times) {
      ...MeetingFragment
      holiday {
        name
      }
    }
  }
  ${MeetingFragment}
`;

export function useCreateMeetingMutation({ section }: $TSFixMe) {
  const [mutate] = useMutation<
    CreateMeetingsMutation,
    CreateMeetingsMutationVariables
  >(createMeetingsMutation);

  return React.useMemo(() => {
    return [
      async (meetingTimes: $TSFixMe) => {
        const {
          // @ts-ignore TS(2339): Property 'createMeetings' does not exist on type '... Remove this comment to see the full error message
          data: { createMeetings: createdMeetings },
        } = await mutate({
          variables: {
            sectionUid: section.uid,
            times: meetingTimes.map((t: $TSFixMe) => ({
              start_time: t.start_time.toISOString(),
              end_time: t.end_time.toISOString(),
            })),
          },
          // We have to refetch to make sure that we get the latest conflict checks
          refetchQueries() {
            return [
              "EditableMeetings",
              {
                query: teacherSectionQuery,
                variables: { uid: section.uid },
              },
            ];
          },
        });
        return createdMeetings;
      },
    ];
  }, [mutate, section.uid]);
}

export const createMeetingsWithLessonUidsMutation = gql`
  mutation createMeetingsWithLessonUids(
    $sectionUid: ID!
    $meetingDetails: [MeetingDetails!]!
  ) {
    createMeetingsWithLessonUids(
      sectionUid: $sectionUid
      meetingDetails: $meetingDetails
    ) {
      ...MeetingFragment
      holiday {
        name
      }
      lesson {
        uid
        title
      }
    }
  }
  ${MeetingFragment}
`;

export function useCreateMeetingsWithLessonUidsMutation({ section }: $TSFixMe) {
  const [mutate] = useMutation<
    CreateMeetingsWithLessonUidsMutation,
    CreateMeetingsWithLessonUidsMutationVariables
  >(createMeetingsWithLessonUidsMutation);

  const mutation = React.useCallback(
    async (meetingDetails: $TSFixMe) => {
      const {
        // @ts-ignore TS(2339): Property 'createMeetings' does not exist on type '... Remove this comment to see the full error message
        data: { createMeetingsWithLessonUids: createdMeetings },
      } = await mutate({
        variables: {
          sectionUid: section.uid,
          meetingDetails: meetingDetails.map(
            ({ start_time, end_time, lesson_uid }: $TSFixMe) => ({
              start_time: start_time.toISOString(),
              end_time: end_time.toISOString(),
              lesson_uid,
            }),
          ),
        },
        // We have to refetch to make sure that we get the latest conflict checks
        refetchQueries() {
          return [
            "EditableMeetings",
            {
              query: teacherSectionQuery,
              variables: { uid: section.uid },
            },
          ];
        },
      });
      return createdMeetings;
    },
    [mutate, section.uid],
  );
  return [mutation];
}

export const updateMeetingsMutation = gql`
  mutation updateMeetings($updateMeetingsInput: [UpdateMeetingInput!]!) {
    updateMeetings(updateMeetingsInput: $updateMeetingsInput) {
      ...MeetingFragment
      holiday {
        name
      }
      lesson {
        uid
        title
      }
    }
  }
  ${MeetingFragment}
`;

export function useUpdateMeetings({
  section,
}: $TSFixMe): [
  (
    updateMeetingsInput: UpdateMeetingsMutationVariables["updateMeetingsInput"],
  ) => Promise<void>,
  { isUpdatingMeetings: boolean },
] {
  const [mutate, { loading }] = useMutation<
    UpdateMeetingsMutation,
    UpdateMeetingsMutationVariables
  >(updateMeetingsMutation);

  const mutation = React.useCallback(
    async (updateMeetingsInput: $TSFixMe) => {
      await mutate({
        variables: {
          updateMeetingsInput,
        },
        // We have to refetch to make sure that we get the latest conflict checks
        refetchQueries() {
          return [
            {
              query: teacherSectionQuery,
              variables: { uid: section.uid },
            },
          ];
        },
      });
    },
    [mutate, section.uid],
  );
  return [mutation, { isUpdatingMeetings: loading }];
}

export const updateMeetingsLessonMutation = gql`
  mutation updateMeetingsLesson(
    $updateMeetingsLessonInput: [UpdateMeetingLessonInput!]!
  ) {
    updateMeetingsLesson(
      updateMeetingsLessonInput: $updateMeetingsLessonInput
    ) {
      ...MeetingFragment
      holiday {
        name
      }
      lesson {
        uid
        title
      }
    }
  }
  ${MeetingFragment}
`;

export function useUpdateMeetingsLesson({
  section,
}: $TSFixMe): [
  (
    updateMeetingsLessonInput: UpdateMeetingsLessonMutationVariables["updateMeetingsLessonInput"],
  ) => Promise<void>,
  { isAssigningOrUnassigningLesson: boolean },
] {
  const [mutate, { loading }] = useMutation<
    UpdateMeetingsLessonMutation,
    UpdateMeetingsLessonMutationVariables
  >(updateMeetingsLessonMutation);

  const mutation = React.useCallback(
    async (updateMeetingsLessonInput: $TSFixMe) => {
      await mutate({
        variables: {
          updateMeetingsLessonInput,
        },
        // We have to refetch to make sure that we get the latest conflict checks
        refetchQueries() {
          return [
            {
              query: teacherSectionQuery,
              variables: { uid: section.uid },
            },
          ];
        },
      });
    },
    [mutate, section.uid],
  );
  return [mutation, { isAssigningOrUnassigningLesson: loading }];
}

export const deleteMeetingMutation = gql`
  mutation deleteMeeting($uid: ID!) {
    deleteMeeting(uid: $uid)
  }
`;

export function useDeleteMeetingMutation({
  section,
}: $TSFixMe): [
  (meetingUid: string) => Promise<ExecutionResult<any>>,
  { isDeletingMeeting: boolean },
] {
  const [mutate, { loading }] = useMutation<
    DeleteMeetingMutation,
    DeleteMeetingMutationVariables
  >(deleteMeetingMutation);

  return React.useMemo(() => {
    return [
      (meetingUid: $TSFixMe) => {
        return mutate({
          variables: { uid: meetingUid },
          // We have to refetch to make sure that we get the latest conflict checks
          refetchQueries() {
            return [
              "EditableMeetings",
              {
                query: teacherSectionQuery,
                variables: { uid: section.uid },
              },
            ];
          },
        });
      },
      { isDeletingMeeting: loading },
    ];
  }, [mutate, loading, section.uid]);
}

export function useCancelOneOnOneMeetingMutation(): [
  ({
    uid,
    cancelReason,
    cancelNote,
    refundAmountCents,
    reducesTeacherPayout,
  }: {
    uid: string;
    cancelReason: string;
    cancelNote: string;
    refundAmountCents?: number;
    reducesTeacherPayout?: boolean;
  }) => Promise<ExecutionResult<any>>,
  { isCancelingOneOnOneMeeting: boolean },
] {
  const [cancelOneOnOneMeeting, { loading }] = useMutation(
    gql`
      mutation cancelOneOnOneMeeting(
        $uid: ID!
        $cancelReason: String!
        $cancelNote: String
        $refundAmountCents: Int
        $reducesTeacherPayout: Boolean
      ) {
        cancelOneOnOneMeeting(
          uid: $uid
          cancelReason: $cancelReason
          cancelNote: $cancelNote
          refundAmountCents: $refundAmountCents
          reducesTeacherPayout: $reducesTeacherPayout
        ) {
          ...MeetingFragment
        }
      }
      ${MeetingFragment}
    `,
    {
      update: (proxy, result) => {
        const canceledMeeting = result?.data?.cancelOneOnOneMeeting;

        proxy.writeFragment({
          fragment: MeetingFragment,
          fragmentName: "MeetingFragment",
          id: dataIdFromObject(canceledMeeting),
          data: canceledMeeting,
        });
      },
    },
  );

  return [
    React.useCallback(
      ({
        uid,
        cancelReason,
        cancelNote,
        refundAmountCents,
        reducesTeacherPayout = true,
      }: {
        uid: string;
        cancelReason: string;
        cancelNote: string;
        refundAmountCents: number;
        reducesTeacherPayout: boolean;
      }) =>
        cancelOneOnOneMeeting({
          variables: {
            uid,
            cancelReason,
            cancelNote,
            refundAmountCents,
            reducesTeacherPayout,
          },
        }),
      [cancelOneOnOneMeeting],
    ),
    { isCancelingOneOnOneMeeting: loading },
  ];
}

export const cancelAllOneOnOneSectionMeetingsMutation = gql`
  mutation cancelAllOneOnOneSectionMeetings(
    $sectionUid: ID!
    $cancelReason: String!
    $cancelNote: String
    $reducesTeacherPayout: Boolean
  ) {
    cancelAllOneOnOneSectionMeetings(
      sectionUid: $sectionUid
      cancelReason: $cancelReason
      cancelNote: $cancelNote
      reducesTeacherPayout: $reducesTeacherPayout
    )
  }
`;

export function useCancelRecurringOneOnOneMeetingMutation(): [
  ({
    uid,
    cancelReason,
    cancelNote,
    refundAmountCents,
    reducesTeacherPayout,
  }: {
    uid: string;
    cancelReason: string;
    cancelNote: string;
    refundAmountCents?: number;
    reducesTeacherPayout?: boolean;
  }) => Promise<ExecutionResult<any>>,
  { isCancelingRecurringOneOnOneMeeting: boolean },
] {
  const [cancelRecurringOneOnOneMeeting, { loading }] = useMutation(
    gql`
      mutation cancelOneOnOneRecurringMeetings(
        $uid: ID!
        $cancelReason: String!
        $cancelNote: String
        $reducesTeacherPayout: Boolean
      ) {
        cancelOneOnOneRecurringMeetings(
          uid: $uid
          cancelReason: $cancelReason
          cancelNote: $cancelNote
          reducesTeacherPayout: $reducesTeacherPayout
        )
      }
    `,
    {
      update: (proxy, result) => {
        if (!result) {
          return;
        }
        for (const meetingUid of result.data.cancelOneOnOneRecurringMeetings) {
          const fragmentData = proxy.readFragment<MeetingFragmentFragment>({
            fragment: MeetingFragment,
            fragmentName: "MeetingFragment",
            id: meetingUid + "Meeting",
          });
          if (fragmentData) {
            const updatedFragment = {
              ...fragmentData,
              deleted_at: new Date().toString(),
            };
            proxy.writeFragment({
              fragment: MeetingFragment,
              id: meetingUid + "Meeting",
              data: updatedFragment,
            });
          }
        }
      },
    },
  );

  return [
    React.useCallback(
      ({
        uid,
        cancelReason,
        cancelNote,
        reducesTeacherPayout = true,
      }: {
        uid: string;
        cancelReason: string;
        cancelNote: string;
        reducesTeacherPayout: boolean;
      }) =>
        cancelRecurringOneOnOneMeeting({
          variables: {
            uid,
            cancelReason,
            cancelNote,
            reducesTeacherPayout,
          },
        }),
      [cancelRecurringOneOnOneMeeting],
    ),
    { isCancelingRecurringOneOnOneMeeting: loading },
  ];
}

export function useRescheduleMeetingMutation() {
  const [rescheduleMeeting] = useMutation(
    gql`
      mutation rescheduleMeeting(
        $uid: ID!
        $startTime: DateTime!
        $endTime: DateTime!
        $rescheduleFutureRecurringMeetings: Boolean!
      ) {
        rescheduleMeeting(
          uid: $uid
          startTime: $startTime
          endTime: $endTime
          rescheduleFutureRecurringMeetings: $rescheduleFutureRecurringMeetings
        ) {
          ...MeetingFragment
        }
      }
      ${MeetingFragment}
    `,
    {
      refetchQueries: ["EnrolledClassDetails"],
      update: (proxy, result) => {
        if (result?.data?.rescheduleMeeting) {
          for (const meeting of result?.data?.rescheduleMeeting ?? []) {
            proxy.writeFragment({
              fragment: MeetingFragment,
              id: dataIdFromObject(meeting),
              data: meeting,
            });
          }
        }
      },
    },
  );

  return React.useCallback(
    ({
      uid,
      startTime,
      endTime,
      rescheduleFutureRecurringMeetings,
    }: {
      uid: string;
      startTime: Date;
      endTime: Date;
      rescheduleFutureRecurringMeetings: Boolean;
    }) =>
      rescheduleMeeting({
        variables: {
          uid,
          startTime,
          endTime,
          rescheduleFutureRecurringMeetings,
        },
      }),
    [rescheduleMeeting],
  );
}
